import { Outlet, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, MessageSquare, Settings, LogOut, Menu, X, Bell, ChevronDown, ArrowLeft,
  ClipboardList, Dumbbell, Video, FileText, Trophy
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import BetaBanner from '@/components/BetaBanner';
import NotificationBell from '@/components/NotificationBell';
import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { teamId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const { logout } = useAuth();

  useEffect(() => {
    // Load user and teams from localStorage
    const storedUser = localStorage.getItem('team_user');
    const storedTeams = localStorage.getItem('team_teams');
    
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    if (storedTeams) {
      const teamsData = JSON.parse(storedTeams);
      setTeams(teamsData);
      // Find current team
      const current = teamsData.find(t => t.team_id === teamId);
      setCurrentTeam(current);
    }

    // Fetch unread notification count
    fetchUnreadCount();
  }, [teamId]);

  const fetchUnreadCount = async () => {
    const token = localStorage.getItem('team_token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/clubs/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleLogout = () => {
    // Centralized logout clears all role-scoped tokens to prevent the PWA
    // auto-redirect from picking up stale entries from a previous session.
    logout();
    navigate('/unified-login');
  };

  const handleTeamSwitch = (newTeamId) => {
    navigate(`/team/${newTeamId}`);
  };

  // Check if user is manager (lightweight permissions)
  const isManager = user?.role === 'team_manager';

  // Navigation items - Manager has limited items, Coach has full access
  const baseNavItems = [
    { path: `/team/${teamId}`, icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: `/team/${teamId}/athletes`, icon: Users, label: 'Athletes' },
    { path: `/team/${teamId}/sessions`, icon: Calendar, label: 'Sessions' },
    { path: `/team/${teamId}/match-report`, icon: FileText, label: 'Match Report' },
    { path: `/team/${teamId}/schedule-match`, icon: Trophy, label: 'Schedule Match' },
  ];
  
  // Coach-only items (Training Plans, Drill Library, Videos)
  const coachOnlyItems = [
    { path: `/team/${teamId}/training-plans`, icon: ClipboardList, label: 'Training Plans' },
    { path: `/team/${teamId}/drills`, icon: Dumbbell, label: 'Drill Library' },
    { path: `/team/${teamId}/videos`, icon: Video, label: 'Videos' },
  ];
  
  // Messages available for both
  const communicationItems = [
    { path: `/team/${teamId}/messages`, icon: MessageSquare, label: 'Messages' },
  ];
  
  // Combine nav items based on role
  const navItems = isManager 
    ? [...baseNavItems, ...communicationItems]
    : [...baseNavItems, ...coachOnlyItems, ...communicationItems];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  if (!user || !currentTeam) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Founding Beta Banner */}
      <BetaBanner />
      
      <div className="flex flex-1">
        {/* Mobile sidebar toggle */}
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-16 right-4 z-50 p-2.5 bg-card border border-border rounded-sm shadow-lg"
          data-testid="team-mobile-menu-btn"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 top-8 lg:top-0
          w-64 bg-card border-r border-border
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Team Selector */}
            <div className="p-4 border-b border-border">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between text-left h-auto py-3">
                    <div className="flex flex-col items-start">
                      <span className="font-heading text-lg truncate max-w-[180px]">
                        {currentTeam.team_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {currentTeam.category} • {currentTeam.club_name}
                      </span>
                    </div>
                    <ChevronDown size={16} className="text-muted-foreground" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {teams.map((team) => (
                    <DropdownMenuItem 
                      key={team.team_id}
                      onClick={() => handleTeamSwitch(team.team_id)}
                      className={team.team_id === teamId ? 'bg-primary/10' : ''}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{team.team_name}</span>
                        <span className="text-xs text-muted-foreground">{team.category}</span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Role Badge */}
            <div className="px-4 py-2">
              <div className={`inline-flex items-center px-2 py-1 rounded text-xs uppercase ${
                isManager 
                  ? 'bg-blue-500/10 text-blue-400 border border-blue-500/30' 
                  : 'bg-primary/10 text-primary'
              }`}>
                {isManager ? 'Team Manager' : 'Team Coach'}
              </div>
              {isManager && (
                <p className="text-xs text-muted-foreground mt-1">
                  Logistics & Attendance
                </p>
              )}
            </div>

            {/* Back to Private Coaching - Show for coaches who came from private dashboard */}
            {!isManager && (
              <div className="px-4 pb-2">
                <Link
                  to="/coach"
                  className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
                  onClick={() => {
                    // Keep team tokens for potential return
                  }}
                >
                  <ArrowLeft size={14} />
                  <span>Back to Private Coaching</span>
                </Link>
              </div>
            )}

            {/* Navigation */}
            <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive(item.path, item.exact)
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={20} />
                  <span className="text-sm uppercase tracking-wider">{item.label}</span>
                </Link>
              ))}

              {/* Notifications Link */}
              <Link
                to={`/team/${teamId}/notifications`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  isActive(`/team/${teamId}/notifications`)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
                data-testid="nav-notifications"
              >
                <Bell size={20} />
                <span className="text-sm uppercase tracking-wider">Notifications</span>
                {unreadCount > 0 && (
                  <span className="ml-auto bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </Link>
            </nav>

            {/* User section — flex-shrink-0 keeps logout always reachable on PWA */}
            <div className="p-4 border-t border-border flex-shrink-0">
              <div className="mb-4 px-4">
                <p className="text-sm font-medium truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <Button 
                variant="ghost" 
                className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut size={20} />
                <span className="text-sm uppercase tracking-wider">Logout</span>
              </Button>
            </div>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/50 z-30"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 lg:ml-0 min-h-screen overflow-x-hidden">
          {/* Topbar with notification bell */}
          <div className="hidden lg:flex justify-end items-center px-6 pt-4">
            <NotificationBell
              tokenKey="team_token"
              onItemClick={(n) => {
                if (n.team_id) navigate(`/team/${n.team_id}`);
              }}
            />
          </div>
          {/* Mobile bell — sits next to the menu toggle */}
          <div className="lg:hidden fixed top-16 right-16 z-50">
            <NotificationBell
              tokenKey="team_token"
              onItemClick={(n) => {
                if (n.team_id) navigate(`/team/${n.team_id}`);
              }}
            />
          </div>
          <div className="p-4 md:p-6 lg:p-8 pt-6 lg:pt-2">
            <Outlet context={{ user, currentTeam, isManager, teams }} />
          </div>
        </main>
      </div>
    </div>
  );
};

export default TeamLayout;
